import {IPromise, IRootScopeService, ITimeoutService} from "angular";
import {regNavn} from "felles/tjenester/tredjepartsTjenestenavn";
import {Melding, MeldingTilBruker, Varighet} from "felles/tjenester/MeldingTilBruker";
import {SystemmeldingRessurs} from "felles/tjenester/ressurser/SystemmeldingRessurs";
import {Autentisering} from "felles/tjenester/Autentisering";
import {l} from "types/typescriptdefinisjoner_lederplan";
import {ModelloppdateringType} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {Debugger} from "debug";

const debug: Debugger = require("debug")("SystemmeldingApplikasjonTjeneste");

/**
 * Tjeneste for visning og sanntidssynkronisering av systemmeldinger inne i applikasjonen
 * for innloggede brukere.
 */
export class SystemmeldingApplikasjonTjeneste {
    static regNavn = "wt.SystemmeldingApplikasjonTjeneste";

    static forInjisering() {
        return [
            regNavn.ng.$rootScope,
            regNavn.ng.$timeout,
            MeldingTilBruker.regNavn,
            SystemmeldingRessurs.regNavn,
            SystemmeldingApplikasjonTjeneste
        ];
    }

    constructor(
      private $rootScope: IRootScopeService,
      private $timeout: ITimeoutService,
      private meldingTilBruker: MeldingTilBruker,
      private systemmeldingRessurs: SystemmeldingRessurs) {}

    private aktivMelding: Melding | null = null;
    private aktivSystemMeldingId: string | null = null;
    private aktivTimeout: IPromise<any> | null = null;

    private apiOppslagsløpenummer = 0; //  // Økes ved nye API-oppslag og ugyldiggjøring, slik at utdaterte svar ignoreres

    public start(): void {
        this.$rootScope.$on(Autentisering.brukerAutentisertEvent, () => {
            void this.hentOgVisAktivMelding();
        });

        this.$rootScope.$on(Autentisering.brukerLoggetUtEvent, () => {
            this.ugyldiggjørPågåendeOppslag();
            this.fjernAktivMelding();
        });

        this.$rootScope.$on(ModelloppdateringType.SYSTEMMELDING_OPPRETTET, (_event, modell: l.SystemMeldingModell) => {
            this.håndterSystemmeldingOpprettet(modell);
        });

        this.$rootScope.$on(ModelloppdateringType.SYSTEMMELDING_SLETTET, (_event, slettetId: any) => {
            this.håndterSystemmeldingSlettet(slettetId);
        });
    }

    public hentOgVisAktivMelding(): PromiseLike<void> {
        const oppslagsgenerasjon = ++this.apiOppslagsløpenummer;
        return this.systemmeldingRessurs.hentAktivForType(l.Meldingstype.applikasjonHoved)
          .then(
            melding => {
                if (oppslagsgenerasjon !== this.apiOppslagsløpenummer) {
                    return;
                }
                if (melding) {
                    this.visMelding(melding);
                } else {
                    this.fjernAktivMelding();
                }
            },
            feil => {
                if (oppslagsgenerasjon === this.apiOppslagsløpenummer) {
                    debug("Feil ved henting av aktiv systemmelding", feil);
                }
            }
          );
    }

    public visMelding(systemMelding: l.SystemMeldingModell): void {
        this.fjernAktivMelding();

        if (!systemMelding || systemMelding.meldingstype !== l.Meldingstype.applikasjonHoved) {
            return;
        }

        if (systemMelding.gyldigTil) {
            const gyldigTilTid = new Date(systemMelding.gyldigTil).getTime();
            const nåTid = Date.now();
            const gjenståendeTidMs = gyldigTilTid - nåTid;
            if (gjenståendeTidMs <= 0) {
                debug("Systemmelding er allerede utløpt, vises ikke", systemMelding);
                return;
            }
            this.aktivTimeout = this.$timeout(() => {
                this.fjernAktivMelding();
            }, gjenståendeTidMs);
        }

        this.aktivSystemMeldingId = systemMelding.id;

        switch (systemMelding.varseltype) {
            case l.Varseltype.advarsel:
                this.aktivMelding = this.meldingTilBruker.advarselmelding(systemMelding.melding, Varighet.klikkBort);
                break;
            case l.Varseltype.feil:
                this.aktivMelding = this.meldingTilBruker.feilmelding(systemMelding.melding, Varighet.klikkBort);
                break;
            case l.Varseltype.info:
            default:
                this.aktivMelding = this.meldingTilBruker.infomelding(systemMelding.melding, Varighet.klikkBort);
                break;
        }
    }

    public håndterSystemmeldingOpprettet(systemMelding: l.SystemMeldingModell): void {
        this.ugyldiggjørPågåendeOppslag();
        if (systemMelding && systemMelding.meldingstype === l.Meldingstype.applikasjonHoved) {
            this.visMelding(systemMelding);
        }
    }

    public håndterSystemmeldingSlettet(slettetId: any): void {
        this.ugyldiggjørPågåendeOppslag();
        const id = typeof slettetId === "string" ? slettetId : (slettetId?.id ?? slettetId);
        if (this.aktivSystemMeldingId && this.aktivSystemMeldingId === id) {
            this.fjernAktivMelding();
        }
    }

    public fjernAktivMelding(): void {
        if (this.aktivTimeout) {
            this.$timeout.cancel(this.aktivTimeout);
            this.aktivTimeout = null;
        }
        if (this.aktivMelding) {
            this.meldingTilBruker.fjernMelding(this.aktivMelding);
            this.aktivMelding = null;
        }
        this.aktivSystemMeldingId = null;
    }

    private ugyldiggjørPågåendeOppslag(): void {
        this.apiOppslagsløpenummer++;
    }

    public hentAktivSystemMeldingId(): string | null {
        return this.aktivSystemMeldingId;
    }

    public hentAktivMelding(): Melding | null {
        return this.aktivMelding;
    }
}
